
import { Icon } from "@iconify/react/dist/iconify.js";
import { useEffect, useRef, useReducer, useState, useCallback } from "react";
import { FaFilter } from "react-icons/fa";
import { Link, useNavigate, useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import './Dashbord.css';
import "jspdf-autotable";
import { api, uploadPath } from "../helper/api.interceptor";
import Swal from "sweetalert2";

// Reducer for filter state
const filterReducer = (state, action) => {
  switch (action.type) {
    case "SET_FILTER":
      return { ...state, [action.key]: action.value };
    case "SET_FILTERS":
      return { ...state, ...action.filters };
    case "RESET_FILTERS":
      return {
        search: "",
        status: "",
        yearsOfExperience: "",
        createdFrom: "",
        createdTo: "",
        position: "",
        location: "",
        column: "",
        order: "",
      };
    default:
      return state;
  }
};

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const statusOptions = useRef({
    status: ["new", "call_for_interview", "on_hold", "reject", "ask_for_more_work", "application_closed"],
    positions: [],
    locations: [],
    statusMapRef: ["New", "Call For Interview", "On Hold", "Reject", "Ask For More Work", "Application Closed"],
  });

  // Initialize filter state from URL params
  const getInitialFilterState = useCallback(() => {
    const params = new URLSearchParams(location.search);
    return {
      search: params.get("search") || "",
      status: params.get("status") || "",
      yearsOfExperience: params.get("yearsOfExperience") || "",
      createdFrom: params.get("createdFrom") || "",
      createdTo: params.get("createdTo") || "",
      position: params.get("position") || "",
      location: params.get("location") || "",
      column: params.get("column") || "",
      order: params.get("order") || "",
    };
  }, [location.search]);

  const [filterState, dispatchFilter] = useReducer(filterReducer, getInitialFilterState());
  const [selectedRows, setSelectedRows] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [profile, setProfile] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    totalPages: 1,
    totalCount: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const isInitialMount = useRef(true);

  // Debounce function to prevent rapid URL updates
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const fetchPositions = async () => {
    try {
      const data = await api.fetchJobPositions();

      console.log("Position API:", data);

      statusOptions.current.positions = (data?.positions || []).map((item) => ({
        value: item,
        label: item,
      }));
    } catch (err) {
      console.error("Error fetching positions:", err);
    }
  };
  useEffect(() => {
    fetchPositions();
  }, []);
  // Sync URL with filter state and pagination
  const syncUrlWithState = useCallback(() => {
    const params = new URLSearchParams();
    Object.entries(filterState).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    params.set("page", currentPage);
    params.set("limit", itemsPerPage);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [filterState, currentPage, itemsPerPage, navigate, location.pathname]);

  // Handle URL changes and initialize state
  useEffect(() => {
    if (isInitialMount.current) {
      const params = new URLSearchParams(location.search);
      const newFilters = getInitialFilterState();
      dispatchFilter({ type: "SET_FILTERS", filters: newFilters });
      setCurrentPage(Number(params.get("page")) || 1);
      setItemsPerPage(Number(params.get("limit")) || 10);
      isInitialMount.current = false;
    }
  }, [location.search, getInitialFilterState]);

  // Sync URL when state changes (debounced)
  const debouncedSyncUrl = useCallback(debounce(syncUrlWithState, 100), [syncUrlWithState]);

  useEffect(() => {
    if (!isInitialMount.current) {
      debouncedSyncUrl();
    }
  }, [filterState, currentPage, itemsPerPage, debouncedSyncUrl]);

  const fetchCandidateData = async (query = {}) => {
    try {
      const { inquiries, locations, pagination: pg } = await api.fetchInquiries(query);
      setTableData(inquiries);
      setPagination(pg);
      statusOptions.current.locations = (locations || []).map((i) => ({
        value: i,
        label: i,
      }));
      // statusOptions.current.positions = (positions || []).map((i) => ({
      //   value: i,
      //   label: i,
      // }));
    } catch (error) {
      console.error("Error fetching candidates:", error);
    }
  };

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const data = await api.myprofile();
        if (data) {
          setProfile({
            username: data.username,
            email: data.email,
            contact: data.contact,
            profileImage: data.profileImage
              ? `${process.env.REACT_APP_UPLOAD_PATH}${data.profileImage}`.replace("//uploads", "/uploads")
              : "assets/images/profileimg.webp",
          });
        }
      } catch (error) {
        console.error("Error fetching profile data:", error);
      }
    };
    fetchProfileData();
  }, []);

  const applyFilters = useCallback(async (page = currentPage) => {
    const filters = {
      ...filterState,
      page,
      limit: itemsPerPage,
    };
    await fetchCandidateData(filters);
  }, [filterState, itemsPerPage, currentPage]);

  useEffect(() => {
    if (!isInitialMount.current) {
      applyFilters();
    }
  }, [applyFilters]);

  const resetFilters = () => {
    dispatchFilter({ type: "RESET_FILTERS" });
    setCurrentPage(1);
    setItemsPerPage(10);
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const dropdownRef = useRef(null);

  const handleOutsideClick = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setOpenDropdownId(null);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  // const currentItems = tableData.slice(indexOfFirstItem, indexOfLastItem);
  const currentItems = tableData;
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const toggleRowSelection = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    setSelectedRows((prev) => {
      const currentPageIds = tableData.map((row) => row.id);
      const allSelected = currentPageIds.every((id) => prev.includes(id));
      if (allSelected) {
        // Deselect all current page items
        return prev.filter((id) => !currentPageIds.includes(id));
      }
      // Select all current page items, preserving previous selections
      return [...new Set([...prev, ...currentPageIds])];
    });
  };
  const exportSelectedToExcel = () => {
    if (!XLSX) {
      alert("Excel export is not available");
      return;
    }
    if (selectedRows.length === 0) {
      alert("Please select at least one row to export");
      return;
    }
    const dataToExport = tableData
      .filter((item) => selectedRows.includes(item.id))
      .map((rowData, iElem) => {
        let rowStatus;
        const findIndex = statusOptions.current.status.findIndex((i) => i === rowData.status);
        if (findIndex > -1) {
          rowStatus = statusOptions.current.statusMapRef[findIndex];
        } else {
          rowStatus = rowData.status;
        }
        return {
          No: iElem + 1,
          "Date of Application": new Date(rowData.createdAt).toISOString().split("T")[0],
          "Full Name": rowData.fullName,
          Experience: String(`${rowData.yearsOfExperience} years`).replace("null years", "NA"),
          Location: rowData.location,
          Position: rowData.position,
          "Year of Graduation Bachelor's": rowData?.graduationYearBachelor || "NA",
          "Mobile Number": rowData.mobileNumber,
          "Email ID": rowData.email,
          Status: rowStatus,
        };
      });
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Selected Candidates");
    XLSX.writeFile(workbook, `selected_candidates_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportRowToExcel = (rowData) => {
    if (!XLSX) {
      alert("Excel export is not available");
      return;
    }
    let rowStatus;
    const findIndex = statusOptions.current.status.findIndex((i) => i === rowData.status);
    if (findIndex > -1) {
      rowStatus = statusOptions.current.statusMapRef[findIndex];
    } else {
      rowStatus = rowData.status;
    }
    // Create vertical data structure
    const dataToExport = [
      ["Field", "Value"],
      ["No", 1],
      ["Date of Application", new Date(rowData.createdAt).toISOString().split("T")[0]],
      ["Full Name", rowData.fullName],
      ["Experience", String(`${rowData.yearsOfExperience} years`).replace("null years", "NA")],
      ["Location", rowData.location],
      ["Position", rowData.position],
      ["Year of Graduation Bachelor's", rowData?.graduationYearBachelor || "NA"],
      ["Mobile Number", rowData.mobileNumber],
      ["Email ID", rowData.email],
      ["Status", rowStatus],
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Candidate");
    XLSX.writeFile(workbook, `candidate_${rowData.id}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const deletequery = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action will delete the query permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });
    if (result.isConfirmed) {
      try {
        await api.deleteQueryies(id);
        await applyFilters();
        Swal.fire("Deleted!", "The query has been deleted.", "success");
      } catch (error) {
        Swal.fire("Error", "Failed to delete the query.", "error");
      }
    }
  };

  const recursiveDelete = async () => {
    if (selectedRows && Array.isArray(selectedRows) && selectedRows.length) {
      const deletePromise = selectedRows.map((elem) => api.deleteQueryies(elem));
      await Promise.all(deletePromise);
      await applyFilters();
    }
  };

  const updateQuery = async (id, status) => {
    await api.updateQueryies(id, { status });
    await applyFilters();
  };

  const handleSort = (column) => {
    const currentSortBy = filterState.column;
    const currentOrder = filterState.order || "asc";
    const newOrder = currentSortBy === column && currentOrder === "asc" ? "desc" : "asc";
    dispatchFilter({ type: "SET_FILTER", key: "column", value: column });
    dispatchFilter({ type: "SET_FILTER", key: "order", value: newOrder });
  };

  const [selectedRowId, setSelectedRowId] = useState(null);
  const [remarkInput, setRemarkInput] = useState("");
  const [showRemarkModal, setShowRemarkModal] = useState(false);

  const openRemarkModal = (row) => {
    setSelectedRowId(row.id);
    setRemarkInput(row.remark || "");
    setShowRemarkModal(true);
  };

  const handleRemarkSubmit = async () => {
    try {
      await api.updateQueryies(selectedRowId, { remark: remarkInput });
      setShowRemarkModal(false);
      setSelectedRowId(null);
      await applyFilters();
    } catch (err) {
      console.error("Failed to update remark", err);
    }
  };

  useEffect(() => {
    fetchPositions();
  }, []);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const onFilterSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    closeModal();
  };

  return (
    <div className="bg-white">
      {/* Header unchanged */}
      <div className="d-flex justify-content-between p-3 align-items-center">
        <Link to="/" className="">
          <img src="assets/images/kapil-logo.png" alt="site logo" className="light-logo" />
        </Link>
        <Link to="/job-opening-management" className="job-openings-btn">
          Job Openings Management
        </Link>
        <div className="dropdown">
          <button className="d-flex justify-content-center align-items-center rounded-circle" type="button" data-bs-toggle="dropdown">
            {profile?.profileImage ? (
              <img src={profile.profileImage} alt="image_user" className="w-40-px h-40-px object-fit-cover rounded-circle" />
            ) : (
              <img src="assets/images/user.png" alt="image_user" className="w-40-px h-40-px object-fit-cover rounded-circle" />
            )}
          </button>
          <div className="dropdown-menu to-top dropdown-menu-sm">
            <ul className="to-top-list">
              <li>
                <Link className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3" to="/myprofile">
                  <Icon icon="solar:user-linear" className="icon text-xl" /> My Profile
                </Link>
              </li>
              <li>
                <a className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-danger d-flex align-items-center gap-3" href={null} onClick={api.signOut}>
                  <Icon icon="lucide:power" className="icon text-xl" /> Log Out
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="d-flex flex-wrap justify-content-end align-items-center gap-3 mb-3 border-top border-bottom p-3" style={{ backgroundColor: "#eeeeee" }}>
        <form className="navbar-search" onSubmit={(e) => e.preventDefault()}>
          <input
            type="text"
            className="bg-base h-40-px w-auto"
            name="search"
            placeholder="Search by Name or Email"
            value={filterState.search}
            onChange={(e) => {
              dispatchFilter({ type: "SET_FILTER", key: "search", value: e.target.value });
              setCurrentPage(1);
            }}
          />
          <Icon icon="ion:search-outline" className="icon" />
        </form>

        <select
          className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
          name="status"
          value={filterState.status}
          onChange={(e) => {
            dispatchFilter({ type: "SET_FILTER", key: "status", value: e.target.value });
            setCurrentPage(1);
          }}
        >
          <option value="">Select Status</option>
          {Array.isArray(statusOptions.current.status) &&
            statusOptions.current.status.map((status, index) => (
              <option key={index} value={status}>
                {statusOptions.current.statusMapRef[index]}
              </option>
            ))}
        </select>

        <button type="button" className="btn btn-dark text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2" onClick={openModal}>
          <FaFilter /> Filter
        </button>

        <button type="button" className="btn btn-outline-danger text-sm btn-sm px-12 py-12 radius-8" onClick={resetFilters}>
          Clear Filters
        </button>

        <div className="dropdown">
          <button className="btn px-18 py-11 text-primary-light" type="button" data-bs-toggle="dropdown" aria-expanded="false">
            <Icon icon="entypo:dots-three-vertical" className="menu-icon" />
          </button>
          <ul className="dropdown-menu">
            <li>
              <Link
                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900"
                onClick={exportSelectedToExcel}
                disabled={selectedRows.length === 0}
                title={selectedRows.length === 0 ? "Select rows to enable export" : "Export selected rows"}
              >
                Export
              </Link>
              <Link
                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900"
                onClick={recursiveDelete}
                disabled={selectedRows.length === 0}
                title={selectedRows.length === 0 ? "Select rows to delete" : "delete selected rows"}
              >
                Delete
              </Link>
            </li>
          </ul>
        </div>
      </div>

      {/* Filter Badges */}
      <div className="d-flex flex-wrap gap-2 mb-3 px-3">
        {filterState.search && (
          <span className="badge bg-dark text-white d-flex align-items-center" style={{ padding: "10px", fontSize: "15px" }}>
            Search: {filterState.search}
            <button className="btn-close btn-close-white ms-2 fs-10" onClick={() => { setCurrentPage(1); return dispatchFilter({ type: "SET_FILTER", key: "search", value: "" }) }} />
          </span>
        )}
        {filterState.location && (
          <span className="badge bg-dark text-white d-flex align-items-center" style={{ padding: "10px", fontSize: "15px" }}>
            Locations: {filterState.location}
            <button className="btn-close btn-close-white ms-2 fs-10" onClick={() => { setCurrentPage(1); return dispatchFilter({ type: "SET_FILTER", key: "location", value: "" }) }} />
          </span>
        )}
        {filterState.position && (
          <span className="badge bg-primary d-flex align-items-center" style={{ padding: "10px", fontSize: "15px" }}>
            Positions: {filterState.position}
            <button className="btn-close btn-close-white ms-2 fs-10" onClick={() => { setCurrentPage(1); return dispatchFilter({ type: "SET_FILTER", key: "position", value: "" }) }} />
          </span>
        )}
        {filterState.yearsOfExperience && (
          <span className="badge bg-primary d-flex align-items-center" style={{ padding: "10px", fontSize: "15px" }}>
            Experience: {filterState.yearsOfExperience}+ years
            <button className="btn-close btn-close-white ms-2 fs-10" onClick={() => { setCurrentPage(1); return dispatchFilter({ type: "SET_FILTER", key: "yearsOfExperience", value: "" }) }} />
          </span>
        )}
        {filterState.createdFrom && filterState.createdTo && (
          <span className="badge bg-primary d-flex align-items-center" style={{ padding: "10px", fontSize: "15px" }}>
            Date: {filterState.createdFrom} to {filterState.createdTo}
            <button
              className="btn-close btn-close-white ms-2 fs-10"
              onClick={() => {
                dispatchFilter({ type: "SET_FILTER", key: "createdFrom", value: "" });
                dispatchFilter({ type: "SET_FILTER", key: "createdTo", value: "" });
                setCurrentPage(1);
              }}
            />
          </span>
        )}
        {filterState.status && (
          <span className="badge bg-primary d-flex align-items-center" style={{ padding: "10px", fontSize: "15px" }}>
            Status:{(() => {
              let rowStatus;
              const findIndex = statusOptions.current.status.findIndex((i) => i === filterState.status);
              if (findIndex > -1) {
                rowStatus = statusOptions.current.statusMapRef[findIndex];
              } else {
                rowStatus = filterState.status;
              }
              return rowStatus
            })()}
            <button className="btn-close btn-close-white ms-2 fs-10" onClick={() => dispatchFilter({ type: "SET_FILTER", key: "status", value: "" })} />
          </span>
        )}
      </div>

      {/* Table */}
      <div className="card basic-data-table mt-4">
        <div className="card-body">
          <div className="table-responsive scroll-sm">
            <table id="dataTable" className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th scope="col">
                    <div className="d-flex align-items-center gap-10">
                      <div className="form-check style-check d-flex align-items-center">
                        <input
                          className="form-check-input radius-4 border input-form-dark"
                          type="checkbox"
                          name="checkbox"
                          id="selectAll"
                          checked={currentItems.length > 0 && currentItems.every((item) => selectedRows.includes(item.id))}
                          onChange={toggleSelectAll}
                        />
                      </div>
                      S.L
                    </div>
                  </th>
                  <th scope="col">Full Name</th>
                  <th scope="col">Email ID</th>
                  <th scope="col">Date of Application</th>
                  <th scope="col" className="cursor-pointer" onClick={() => handleSort("yearsOfExperience")}>
                    Experience
                    {filterState.column === "yearsOfExperience" &&
                      (filterState.order === "asc" ? (
                        <img src="assets/images/sort-img/asc.png" className="light-logo sortIcon" />
                      ) : (
                        <img src="assets/images/sort-img/desc.png" className="light-logo sortIcon" />
                      )) || <img src="assets/images/sort-img/sorting.png" className="light-logo sortIcon" />}
                  </th>
                  <th scope="col" className="cursor-pointer" onClick={() => handleSort("location")}>
                    Location
                    {filterState.column === "location" &&
                      (filterState.order === "asc" ? (
                        <img src="assets/images/sort-img/asc.png" className="light-logo sortIcon" />
                      ) : (
                        <img src="assets/images/sort-img/desc.png" className="light-logo sortIcon" />
                      )) || <img src="assets/images/sort-img/sorting.png" className="light-logo sortIcon" />}
                  </th>
                  <th scope="col" className="cursor-pointer" onClick={() => handleSort("position")}>
                    Position
                    {filterState.column === "position" &&
                      (filterState.order === "asc" ? (
                        <img src="assets/images/sort-img/asc.png" className="light-logo sortIcon" />
                      ) : (
                        <img src="assets/images/sort-img/desc.png" className="light-logo sortIcon" />
                      )) || <img src="assets/images/sort-img/sorting.png" className="light-logo sortIcon" />}
                  </th>
                  <th scope="col" className="text-center cursor-pointer" onClick={() => handleSort("status")}>
                    Status
                    {filterState.column === "status" &&
                      (filterState.order === "asc" ? (
                        <img src="assets/images/sort-img/asc.png" className="light-logo sortIcon" />
                      ) : (
                        <img src="assets/images/sort-img/desc.png" className="light-logo sortIcon" />
                      )) || <img src="assets/images/sort-img/sorting.png" className="light-logo sortIcon" />}
                  </th>
                  <th scope="col" className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(tableData) &&
                  tableData.map((row, index) => (
                    <tr key={row.id}>
                      <td>
                        <div className="d-flex align-items-center gap-10">
                          <div className="form-check style-check d-flex align-items-center">
                            <input
                              className="form-check-input radius-4 border border-neutral-400"
                              type="checkbox"
                              name="checkbox"
                              checked={selectedRows.includes(row.id)}
                              onChange={() => {
                                setSelectedRows((prev) =>
                                  prev.includes(row.id)
                                    ? prev.filter((id) => id !== row.id)
                                    : [...prev, row.id]
                                );
                              }}
                            />
                          </div>
                          {String(indexOfFirstItem + index + 1).padStart(2, "0")}
                        </div>
                      </td>
                      <td>{row.fullName}</td>
                      <td>{row.email}</td>
                      <td>{new Date(row.createdAt).toISOString().split("T")[0]}</td>
                      <td>{row.yearsOfExperience} years</td>
                      <td>{row.location}</td>
                      <td>{row.position}</td>
                      <td className="position-relative text-center">
                        <span
                          onClick={() => setOpenDropdownId(openDropdownId === row.id ? null : row.id)}
                          className={`${row.status === "new"
                            ? "bg-info-focus text-info-600 border-info-main"
                            : row.status === "intervied"
                              ? "bg-primary-focus text-primary-600 border-primary-main"
                              : row.status === "on_hold"
                                ? "bg-warning-focus text-warning-600 border-warning-main"
                                : row.status === "call_for_interview"
                                  ? "bg-success-focus text-success-600 border-success-main"
                                  : "bg-danger-focus text-danger-600 border-danger-main"
                            } dropdowncustom border px-24 py-2 radius-4 fw-medium text-sm inline-block`}
                        >
                          {(() => {
                            const findIndex = statusOptions.current.status.findIndex((i) => i === row.status);
                            return findIndex > -1 ? statusOptions.current.statusMapRef[findIndex] : row.status;
                          })()}
                          <img
                            src="data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 16 16%27%3e%3cpath fill=%27none%27 stroke=%27%23343a40%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27 stroke-width=%272%27 d=%27m2 5 6 6 6-6%27/%3e%3c/svg%3e"
                            width={"20"}
                            height={"20"}
                            alt=""
                          />
                        </span>
                        {openDropdownId === row.id && (
                          <div
                            ref={dropdownRef}
                            className="drodownoep position-absolute mt-2 bg-white border radius-8 shadow-sm px-2 py-1"
                            style={{ maxWidth: "160px", left: "0", right: "0", margin: "0 auto", zIndex: "9" }}
                          >
                            {statusOptions.current.status.map((s, i) => (
                              <div
                                key={i}
                                onClick={() => {
                                  updateQuery(row.id, s);
                                  setOpenDropdownId(null);
                                }}
                                className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm text-nowrap"
                              >
                                {(() => {
                                  const findIndex = statusOptions.current.status.findIndex((i) => i === s);
                                  return findIndex > -1 ? statusOptions.current.statusMapRef[findIndex] : s;
                                })()}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="text-center">
                        <Link
                          to="#"
                          className="w-32-px h-32-px me-8 bg-warning-light text-warning-800 rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => openRemarkModal(row)}
                          style={{ visibility: !row.remark ? 'hidden' : "" }}
                        >
                          <Icon icon="iconamoon:edit" />
                        </Link>
                        <Link
                          to={`/details/${row?.id || ""}`}
                          className="w-32-px h-32-px me-8 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center"
                        >
                          <Icon icon="iconamoon:eye-light" />
                        </Link>
                        <Link
                          to="#"
                          className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => deletequery(row.id)}
                        >
                          <Icon icon="mingcute:delete-2-line" />
                        </Link>
                        <button
                          className="btn btn-sm btn-dark radius-8 d-inline-flex align-items-center gap-1"
                          onClick={() => exportRowToExcel(row)}
                        >
                          <Icon icon="solar:download-linear" className="text-xl" />
                          Export
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Remark Modal */}
          {showRemarkModal && (
            <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Edit Remark</h5>
                    <button type="button" className="btn-close" onClick={() => setShowRemarkModal(false)}></button>
                  </div>
                  <div className="modal-body">
                    <textarea
                      rows={6}
                      className="form-control"
                      value={remarkInput}
                      onChange={(e) => setRemarkInput(e.target.value)}
                      placeholder="Enter remark"
                    ></textarea>
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setShowRemarkModal(false)}>Cancel</button>
                    <button className="btn btn-dark" onClick={handleRemarkSubmit}>Save Remark</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pagination */}
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
            <div className="d-flex align-items-center gap-2">
              <span>Show</span>
              <select
                className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
            <span>
              Showing {indexOfFirstItem + 1} to {indexOfFirstItem + Math.min(itemsPerPage, tableData?.length || 0)} of {pagination?.totalCount || 0} entries
            </span>
            <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
              <li className="page-item">
                <button
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md"
                  onClick={() => paginate(1)}
                  disabled={currentPage === 1}
                >
                  <Icon icon="ep:d-arrow-left" />
                </button>
              </li>
              <li className="page-item">
                <button
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md"
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <Icon icon="material-symbols:chevron-left" />
                </button>
              </li>
              {(() => {
                let startPage = 1;
                let endPage = pagination?.totalPages || 1;
                if (pagination?.totalPages && pagination?.totalPages > 5) {
                  if (currentPage <= 3) {
                    startPage = 1;
                    endPage = 5;
                  } else if (currentPage + 2 >= pagination?.totalPages) {
                    startPage = pagination?.totalPages - 4;
                    endPage = pagination?.totalPages;
                  } else {
                    startPage = currentPage - 2;
                    endPage = currentPage + 2;
                  }
                }
                const pages = [];
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <li key={i} className="page-item">
                      <button
                        className={`page-link ${currentPage === i ? "bg-primary-600 text-white" : "bg-neutral-200 text-secondary-light"} fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px`}
                        onClick={() => paginate(i)}
                      >
                        {i}
                      </button>
                    </li>
                  );
                }
                return pages;
              })()}
              <li className="page-item">
                <button
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md"
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === pagination.totalPages}
                >
                  <Icon icon="material-symbols:chevron-right" />
                </button>
              </li>
              <li className="page-item">
                <button
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md"
                  onClick={() => paginate(pagination.totalPages)}
                  disabled={currentPage === pagination.totalPages}
                >
                  <Icon icon="ep:d-arrow-right" />
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Filter Modal */}
      {isModalOpen && (
        <div className="modal show d-block" tabIndex={-1} role="dialog" aria-modal="true" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content radius-16 bg-base">
              <div className="modal-header py-16 px-24 border-top-0 border-start-0 border-end-0">
                <h1 className="modal-title fs-5">Filter</h1>
                <button type="button" className="btn-close" aria-label="Close" onClick={closeModal} />
              </div>
              <div className="modal-body p-24">
                <form onSubmit={onFilterSubmit}>
                  <div className="row">
                    <div className="col-6 mb-20">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-8">Years of Experience</label>
                      <input
                        type="number"
                        className="form-control radius-8"
                        min="0"
                        placeholder="Years"
                        value={filterState.yearsOfExperience}
                        onChange={(e) => { setCurrentPage(1); dispatchFilter({ type: "SET_FILTER", key: "yearsOfExperience", value: e.target.value }) }}
                      />
                    </div>
                    <div className="col-6 mb-20">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-8">Location</label>
                      <select
                        name="location"
                        className="form-select radius-8"
                        value={filterState.location}
                        onChange={(e) => { setCurrentPage(1); dispatchFilter({ type: "SET_FILTER", key: "location", value: e.target.value }) }}
                      >
                        <option value="">Select locations...</option>
                        {statusOptions.current.locations.map((loc) => (
                          <option key={loc.value} value={loc.value}>{loc.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6 mb-20">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-8">Position</label>
                      <select
                        name="position"
                        className="form-select radius-8"
                        value={filterState.position}
                        onChange={(e) => {
                          dispatchFilter({
                            type: "SET_FILTER",
                            key: "position",
                            value: e.target.value,
                          });
                          setCurrentPage(1);
                        }}
                      >
                        <option value="">Select position...</option>
                        {statusOptions.current.positions.map((pos) => (
                          <option key={pos.value} value={pos.value}>{pos.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6 mb-20">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-8">Status</label>
                      <select
                        className="form-select radius-8"
                        value={filterState.status}
                        onChange={(e) => { setCurrentPage(1); dispatchFilter({ type: "SET_FILTER", key: "status", value: e.target.value }) }}
                      >
                        <option value="">Select status...</option>
                        {statusOptions.current.status.map((s, i) => {
                          const findIndex = statusOptions.current.status.findIndex((i) => i === s);
                          return (
                            <option key={i} value={s}>
                              {findIndex > -1 ? statusOptions.current.statusMapRef[findIndex] : s}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <div className="col-12 mb-20">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-8">Application Date Range</label>
                      <div className="d-flex gap-3">
                        <input
                          type="date"
                          className="form-control radius-8"
                          placeholder="From"
                          value={filterState.createdFrom}
                          onChange={(e) => { setCurrentPage(1); return dispatchFilter({ type: "SET_FILTER", key: "createdFrom", value: e.target.value }) }}
                        />
                        <input
                          type="date"
                          className="form-control radius-8"
                          placeholder="To"
                          value={filterState.createdTo}
                          onChange={(e) => { setCurrentPage(1); dispatchFilter({ type: "SET_FILTER", key: "createdTo", value: e.target.value }) }}
                        />
                      </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                      <button
                        type="button"
                        className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-40 py-11 radius-8"
                        onClick={() => {
                          resetFilters();
                          closeModal();
                        }}
                      >
                        Reset All
                      </button>
                      <button
                        type="submit"
                        aria-label="Apply Filters"
                        className="btn bg-dark text-white border border-primary-600 text-md px-48 py-12 radius-8"
                      >
                        Apply Filters
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;